'use client';
import { useCallback } from 'react';
import { createEnterAppAuthState } from '@/contexts/auth/auth-navigation';
import { useEnterHomeLogin } from '@/contexts/auth/use-enter-home-login';
import { useModels } from '@/features/home/hooks/use-models';
import {
    savePendingEnterHomePrompt,
    type EnterHomeCreationType,
} from '@enter/business-components/lib/pending-enter-home-prompt';

type LandingBuildMode = 'apps' | 'agents';

const LANDING_BUILD_RETURN_TO: Record<LandingBuildMode, string> = {
    apps: '/workspace/',
    agents: '/workspace/?mode=agents',
};

export function useHomeCreation(isAuthenticated: boolean) {
    const { login } = useEnterHomeLogin();
    const handleBuildAction = useCallback(
        (mode: LandingBuildMode, _source: string) => {
            void login({
                appState: createEnterAppAuthState(
                    LANDING_BUILD_RETURN_TO[mode],
                ),
            });
        },
        [login],
    );

    // 默认值跟随模型目录，用户在输入组件内的选择由组件自身持有。
    const { models } = useModels();
    const selectedModel = (models.find((model) => model.is_default) ?? models[0])?.id ?? '';

    // 提交回调：未登录时引导登录，已登录时处理消息
    const handleSubmit = useCallback(
        async (
            message: string,
            _files: unknown[],
            creationType: EnterHomeCreationType,
            model: string,
            journeyId?: string,
        ) => {
            const trimmedMessage = message.trim();
            const buildMode: LandingBuildMode =
                creationType === 'agent' ? 'agents' : 'apps';
            const appState = createEnterAppAuthState(
                LANDING_BUILD_RETURN_TO[buildMode],
            );

            if (trimmedMessage) {
                savePendingEnterHomePrompt({
                    message: trimmedMessage,
                    creationType,
                    planMode: false,
                    model,
                    journeyId: journeyId ?? crypto.randomUUID(),
                });
            }

            if (isAuthenticated) {
                window.location.assign(LANDING_BUILD_RETURN_TO[buildMode]);
                return new Promise<void>(() => {});
            }

            try {
                await login({ appState });
            } catch (error) {
                console.error('Submit failed:', error);
                throw error;
            }
        },
        [isAuthenticated, login],
    );

    return { selectedModel, handleBuildAction, handleSubmit };
}
